/**
 * Pure TYPEWRITER state — the reveal math for DialogBox, free of Phaser/timers so
 * it is unit-testable. A driver ticks elapsed ms in; the helper reports how many
 * characters of the full string should now be visible. Tap-to-advance is modeled as
 * "complete now" (reveal everything). Respects reduced-motion (instant reveal).
 */

export interface TypewriterState {
  readonly full: string;
  /** ms per revealed character (0 => instant, for reduced-motion). */
  readonly msPerChar: number;
  /** Characters currently revealed. */
  visibleCount: number;
  /** Accumulated ms since the last character was revealed. */
  accMs: number;
}

export function createTypewriter(full: string, msPerChar: number): TypewriterState {
  const instant = msPerChar <= 0;
  return {
    full,
    msPerChar,
    visibleCount: instant ? full.length : 0,
    accMs: 0,
  };
}

/** True once the whole string is revealed. */
export function isComplete(s: TypewriterState): boolean {
  return s.visibleCount >= s.full.length;
}

/** The currently-visible substring. */
export function visibleText(s: TypewriterState): string {
  return s.full.slice(0, s.visibleCount);
}

/**
 * Advance the typewriter by `deltaMs`. Reveals as many characters as the elapsed
 * time allows (handles big frame gaps gracefully). Mutates + returns the state.
 */
export function advance(s: TypewriterState, deltaMs: number): TypewriterState {
  if (isComplete(s) || s.msPerChar <= 0) {
    s.visibleCount = s.full.length;
    return s;
  }
  s.accMs += deltaMs;
  while (s.accMs >= s.msPerChar && s.visibleCount < s.full.length) {
    s.accMs -= s.msPerChar;
    s.visibleCount += 1;
  }
  return s;
}

/** Reveal everything immediately (tap-to-advance "skip" gesture). */
export function complete(s: TypewriterState): TypewriterState {
  s.visibleCount = s.full.length;
  s.accMs = 0;
  return s;
}
